import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  animate,
  query,
  stagger,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';

/** Drives both the header icon and the illustration of a card. */
export type AiKind =
  | 'biometric'
  | 'chatbot'
  | 'analytics'
  | 'voice'
  | 'workflow'
  | 'personalization'
  | 'fraud';

export interface AiFeature {
  id: AiKind;
  /** Full name, shown once the card is expanded. */
  title: string;
  /** Scannable name for the collapsed rail, where the column is ~170px. */
  short: string;
  description: string;
}

@Component({
  selector: 'app-ai-solutions',
  templateUrl: './ai-solutions.component.html',
  styleUrls: ['./ai-solutions.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('reveal', [
      state('hidden', style({ opacity: 0, transform: 'translateY(50px)' })),
      state('shown', style({ opacity: 1, transform: 'none' })),
      transition('hidden => shown', animate('700ms cubic-bezier(0.22, 1, 0.36, 1)')),
    ]),
    trigger('gridReveal', [
      transition('hidden => shown', [
        query(
          'app-ai-feature-card',
          style({ opacity: 0, transform: 'translateY(50px)' }),
          { optional: true }
        ),
        query(
          'app-ai-feature-card',
          stagger(100, [
            animate(
              '700ms cubic-bezier(0.22, 1, 0.36, 1)',
              style({ opacity: 1, transform: 'none' })
            ),
          ]),
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class AiSolutionsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('aiRoot') aiRoot?: ElementRef<HTMLElement>;

  /** 'hidden' until the section scrolls into view, then 'shown' — once. */
  readonly revealState = signal<'hidden' | 'shown'>('hidden');

  /** The single expanded card. Chatbot is featured by default. */
  readonly selectedCard = signal<AiKind>('chatbot');

  reduced = false;

  /** Order matches the reference: lead card, chatbot, then the rest. */
  readonly features: AiFeature[] = [
    {
      id: 'biometric',
      title: 'AI-Powered Biometric Authentication Systems',
      short: 'Biometric Authentication',
      description:
        'Secure, frictionless access with face and fingerprint recognition, protecting user accounts while removing the friction of passwords.',
    },
    {
      id: 'chatbot',
      title: 'In-App NLP Chatbot Integration',
      short: 'NLP Chatbot',
      description:
        'Our AI-powered chatbot integration enables intelligent, real-time conversations, improves customer support, automates responses, and delivers a seamless in-app user experience.',
    },
    {
      id: 'analytics',
      title: 'AI-Powered Predictive Analytics',
      short: 'Predictive Analytics',
      description:
        'Turn raw product data into forecasts your team can act on, surfacing trends, churn risk and demand before they affect the business.',
    },
    {
      id: 'voice',
      title: 'AI-Powered Voice & Image Search',
      short: 'Voice & Image Search',
      description:
        'Let users search by speaking or snapping a photo, with natural language and visual recognition that shortens the path to the right result.',
    },
    {
      id: 'workflow',
      title: 'AI-Driven Workflow Automation',
      short: 'Workflow Automation',
      description:
        'Automate repetitive operations end to end, routing tasks, approvals and data between systems so your team focuses on higher-value work.',
    },
    {
      id: 'personalization',
      title: 'AI-Based Personalization Engine',
      short: 'Personalization Engine',
      description:
        'Adapt content, offers and journeys to each user in real time, driving engagement and retention through relevance rather than guesswork.',
    },
    {
      id: 'fraud',
      title: 'AI-Powered Fraud Detection & Risk Management',
      short: 'Fraud Detection',
      description:
        'Spot anomalies as they happen with models that score every transaction, blocking fraud early while keeping genuine users moving.',
    },
  ];

  private io?: IntersectionObserver;
  private offScroll?: () => void;

  constructor(
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  trackById(_index: number, item: AiFeature): string {
    return item.id;
  }

  select(id: AiKind): void {
    this.selectedCard.set(id);
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.revealState.set('shown');
      return;
    }

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) {
      this.revealState.set('shown');
      return;
    }

    const el = this.aiRoot?.nativeElement;
    if (!el) return;

    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) {
      this.revealState.set('shown');
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.show();
      },
      { threshold: 0, rootMargin: '0px 0px -12% 0px' }
    );
    this.zone.runOutsideAngular(() => this.io?.observe(el));

    /* Fail-safe. The header sits at opacity 0 until this flips, so if the
       observer never reports (throttled tab, layout shift under it, an
       ancestor that swallows the callback) the heading would be lost for
       good. A plain scroll check can't get stuck. */
    const onScroll = () => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) this.show();
    };
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    });
    this.offScroll = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }

  /** Reveal once, from whichever watcher gets there first. */
  private show(): void {
    if (this.revealState() === 'shown') return;
    // set inside the zone so OnPush picks the state change up
    this.zone.run(() => this.revealState.set('shown'));
    this.teardown();
  }

  private teardown(): void {
    this.io?.disconnect();
    this.io = undefined;
    this.offScroll?.();
    this.offScroll = undefined;
  }

  ngOnDestroy(): void {
    this.teardown();
  }
}
